import { useMemo } from 'react';
import { LocalStorage, KEYS } from '@/utils/LocalStorage';
import { useUserDetails } from '@/hooks/useUserDetails';
import { useHasRecommendation } from '@/hooks/useHasRecommendation';
import TokenManager from '@viome/viome-base/src/api/TokenManager';
import {
  useSMPSessionInit,
  useGetSubscriptions,
  useGetSubscriptionDetail,
  useGetPlatformState,
  useGetCustomerDiscounts,
  useGetUIMessages,
  useGetChangePlanData,
  useCanSkipOrder,
  useGetSubscriptionState,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type {
  SMPSubscription,
  SMPQueryParams,
  SMPPlanComponent,
  DiscountsResponse,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { isSubscriptionCancelled } from '../utils/smpHelpers';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';

/** Hook to get subscription ID from bootup data */
const useSubscriptionId = (): number | null => {
  const localBootup = LocalStorage.getItem(KEYS.BOOTUP);
  if (!localBootup) return null;

  try {
    const parsed = JSON.parse(localBootup);
    return parsed.subscriptionId ?? null;
  } catch {
    return null;
  }
};

/** Hook to get supplement eligibility flags from bootup data */
const useBootupFlags = () => {
  const localBootup = LocalStorage.getItem(KEYS.BOOTUP);
  if (!localBootup) return { hasPersonalizedSupplementRecommendations: false, isEligibleForSupplements: false };

  try {
    const parsed = JSON.parse(localBootup);
    return {
      hasPersonalizedSupplementRecommendations: parsed.hasPersonalizedSupplementRecommendationsExists ?? false,
      isEligibleForSupplements: parsed.isEligibleForSupplements ?? false,
    };
  } catch {
    return { hasPersonalizedSupplementRecommendations: false, isEligibleForSupplements: false };
  }
};

interface UseSMPDataOptions {
  skip?: {
    discounts?: boolean;
    changePlan?: boolean;
    uiMessages?: boolean;
    canSkip?: boolean;
    holdingPattern?: boolean;
  };
}

/** Main hook to provide all SMP page data */
export const useSMPData = (options?: UseSMPDataOptions) => {
  const {
    discounts: skipDiscounts,
    changePlan: skipChangePlan,
    uiMessages: skipUIMessages,
    canSkip: skipCanSkip,
    holdingPattern: skipHoldingPattern,
  } = options?.skip ?? {};
  const userDetails = useUserDetails();
  const iframeModeState = useIframeModeStore();
  const customerId = String(userDetails.shopifyUserID || iframeModeState.customerId || '');
  const subscriptionIdFromBootup = useSubscriptionId();
  const bootupHasRecommendation = useHasRecommendation();
  const hasRecommendation = bootupHasRecommendation || iframeModeState.hasRecommendation;
  const bootupFlags = useBootupFlags();
  const hasPersonalizedSupplementRecommendations = bootupFlags.hasPersonalizedSupplementRecommendations || iframeModeState.hasPersonalizedSupplementRecommendations;
  const isEligibleForSupplements = bootupFlags.isEligibleForSupplements || iframeModeState.isEligibleForSupplements;

  // Step 1: Initialize SMP session by calling /app/loginByToken
  // This establishes cookies on the SMP domain (test.stage.azure.viome.com)
  // All subsequent SMP API calls depend on this succeeding first.
  const authToken = TokenManager.getAuthToken();
  const {
    data: isSessionReady,
    isLoading: isLoadingSession,
    isError: isErrorSession,
  } = useSMPSessionInit(authToken);

  // Step 2: Fetch all subscriptions (only after session is established)
  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
    isError: isErrorSubscriptions,
  } = useGetSubscriptions(customerId, {
    enabled: !!isSessionReady && !!customerId,
  });

  const subscriptions = subscriptionsData?.subscriptions;
  const platform = subscriptionsData?.platform ?? '';

  // Determine the active subscription:
  // 1. Try bootup subscription ID
  // 2. Prefer active or paused subscriptions (matching legacy filteredSubscriptions selector)
  // 3. Fallback to most recently updated subscription
  const activeSubscription: SMPSubscription | undefined = useMemo(() => {
    if (!subscriptions || subscriptions.length === 0) return undefined;

    if (subscriptionIdFromBootup) {
      const found = subscriptions.find(
        (s) => s.id === subscriptionIdFromBootup,
      );
      if (found) return found;
    }

    const activeSubs = subscriptions.filter(
      (s) => s.subscription_status === 'active' || s.subscription_status === 'paused',
    );
    if (activeSubs.length === 1) return activeSubs[0];
    if (activeSubs.length > 1) {
      return [...activeSubs].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      )[0];
    }

    return [...subscriptions].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    )[0];
  }, [subscriptions, subscriptionIdFromBootup]);

  const subscriptionId = activeSubscription?.id ?? 0;

  const queryParams: SMPQueryParams = useMemo(
    () => ({
      subscriptionId,
      customerId,
    }),
    [subscriptionId, customerId],
  );

  // Step 3: Fetch detailed subscription data (only after session + subscription resolved)
  const {
    data: subscriptionDetail,
    isLoading: isLoadingDetail,
    isError: isErrorDetail,
  } = useGetSubscriptionDetail(queryParams, {
    enabled: !!isSessionReady && !!subscriptionId && !!customerId,
  });

  // Step 4: Fetch platform state (only after session is established)
  const {
    data: platformState,
    isLoading: isLoadingPlatformState,
    isError: isErrorPlatformState,
  } = useGetPlatformState(subscriptionId, {
    enabled: !!isSessionReady && !!subscriptionId,
  });

  const isCancelled = activeSubscription ? isSubscriptionCancelled(activeSubscription.subscription_status) : false;
  const subscriptionsResolved = !!subscriptions && subscriptions.length > 0;

  // Step 5: Fetch customer discounts (skip for cancelled subscriptions — the API errors out)
  // Wait for subscriptions to resolve first so we know the cancellation status before firing.
  const {
    data: discountsRaw,
    isLoading: isLoadingDiscounts,
    isError: isErrorDiscounts,
  } = useGetCustomerDiscounts(customerId, {
    enabled: !skipDiscounts && !!isSessionReady && !!customerId && subscriptionsResolved && !isCancelled,
  });

  // Step 6: Fetch change plan data to get individual product components
  // The subscription detail API returns combined line items (e.g. "Full Body Test w/ Biotics"),
  // but the change plan API returns individual components (test kit, supplements, biotics, etc.)
  const {
    data: changePlanData,
    isLoading: isLoadingChangePlan,
    isError: isErrorChangePlan,
  } = useGetChangePlanData(subscriptionId, {
    enabled: !skipChangePlan && !!isSessionReady && !!subscriptionId,
  });

  // Step 7: Fetch UI messages for subscription management
  const {
    data: uiMessages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
  } = useGetUIMessages('subscription_management', {
    enabled: !skipUIMessages && !!isSessionReady,
  });

  // Step 8: Fetch can_skip status (only when skip order link is visible)
  const showSkipOrder = isEligibleForSupplements && hasPersonalizedSupplementRecommendations && !isCancelled;
  const {
    data: canSkipData,
  } = useCanSkipOrder(subscriptionId, {
    enabled: !skipCanSkip && !!isSessionReady && !!subscriptionId && showSkipOrder,
  });

  // Step 9: Fetch holding pattern state
  const { data: holdingPatternData } = useGetSubscriptionState(
    'HOLDING_PATTERN',
    subscriptionId,
    { enabled: !skipHoldingPattern && !!isSessionReady && !!subscriptionId },
  );

  const canSkip = showSkipOrder ? (canSkipData?.can_skip ?? true) : true;
  const isOnHoldingPatternState = useMemo(() => {
    return Array.isArray(holdingPatternData?.openStates) && holdingPatternData.openStates.length > 0;
  }, [holdingPatternData]);

  const customerDiscount = useMemo((): DiscountsResponse | null => {
    if (!discountsRaw) return null;
    const raw = discountsRaw as unknown as Record<string, unknown>;
    const nested = raw?.data as DiscountsResponse | undefined;
    if (nested?.discountCodeName) return nested;
    const flat = raw as unknown as DiscountsResponse;
    if (flat?.discountCodeName) return flat;
    return null;
  }, [discountsRaw]);

  const isLoading =
    isLoadingSession || isLoadingSubscriptions || isLoadingDetail || isLoadingPlatformState
    || (!skipDiscounts && !isCancelled && isLoadingDiscounts)
    || (!skipChangePlan && isLoadingChangePlan)
    || (!skipUIMessages && isLoadingMessages);
  const isError = isErrorSession || isErrorSubscriptions || isErrorDetail;
  const hasNonCriticalError = isErrorPlatformState || isErrorDiscounts || isErrorChangePlan || isErrorMessages;
  const showMaintenance = platformState?.data?.show_maintenance ?? false;

  // Determine if there's an order in progress from the lacore_order data
  const hasOrderInProgress = useMemo(() => {
    if (!subscriptionDetail?.lacore_order) return false;
    const { current_state } = subscriptionDetail.lacore_order;
    return current_state === 'Placed' || current_state === 'Processing' || current_state === 'Shipped';
  }, [subscriptionDetail]);

  const productComponents: SMPPlanComponent[] = changePlanData?.current_components ?? [];

  return {
    customerId,
    subscriptionId,
    subscriptionDetail,
    subscriptions,
    platform,
    productComponents,
    customerDiscount,
    hasRecommendation,
    hasPersonalizedSupplementRecommendations,
    isEligibleForSupplements,
    hasOrderInProgress,
    canSkip,
    isOnHoldingPatternState,
    showMaintenance,
    uiMessages,
    isLoading,
    isError,
    hasNonCriticalError,
    isSessionReady: !!isSessionReady,
    queryParams,
  };
};
